import React, { useState, useEffect, useRef } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import useScoFormsEnabled from "../../hooks/useScoFormsEnabled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import toast from "react-hot-toast";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import Breadcrumbs from "../../components/Breadcrumbs";
import OffboardingForm from "../../components/offboarding/OffboardingForm";
import {
  EMPTY_OFFBOARDING,
  buildOffboardingPayload,
  validateOffboarding,
} from "../../components/offboarding/offboardingFormData";
import { useAuth } from "../../providers/AuthContext";
import { getScoFormSettings, getUsersByTenant } from "../../api/Users";
import { submitOffboarding } from "../../api/Offboarding";

/**
 * Today's date in yyyy-MM-dd form, read in local time so "today" means the orderer's today
 * rather than UTC's.
 *
 * @returns {string} Today in yyyy-MM-dd form
 */
const todayIsoDate = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

/** Used when the API registered the case but could not say more about the failed schedule. */
const NOT_SCHEDULED_MESSAGE =
  "The offboarding could not be scheduled automatically. Diflex will handle it manually.";

/**
 * The message shown when the submit fails. A 403 and the API's validation texts are the
 * cases the orderer can act on: a 400 carries orderer-facing wording as a plain string
 * ("This user already has an offboarding in progress."), so it is shown as-is instead of
 * being hidden behind the generic failure message.
 *
 * @param {object} error - The rejected axios error
 * @returns {string} The message for the error toast
 */
const submitErrorMessage = (error) => {
  const { status, data } = error?.response || {};

  if (status === 403) return "You are not allowed to offboard users for this customer";
  if (status === 400 && typeof data === "string" && data.trim()) return data.trim();

  return "Failed to send the offboarding request";
};

/**
 * The page chrome shared by the loading state and the loaded page.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - The page body
 */
const PageShell = ({ children }) => (
  <>
    <HeaderBar />
    <div className="flex flex-row w-full">
      <Sidebar />
      <main className="page">
        <div className="page-content">
          <Breadcrumbs />
          {children}
        </div>
      </main>
    </div>
  </>
);

/**
 * Offboard user (epic #288): the customer reports that someone is leaving. The API disables the
 * account on the end date, schedules the deletion and mails the case to Diflex; the orderer gets
 * the case reference back.
 */
const OffboardUserPage = () => {
  const { agreementPublicId, tenantPublicId, userPublicId } = useParams();
  const { enabled, loaded } = useScoFormsEnabled(agreementPublicId);
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [value, setValue] = useState(EMPTY_OFFBOARDING);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const accessToken = await getAccessToken();
        const [formSettings, tenantUsers] = await Promise.all([
          getScoFormSettings(accessToken).catch(() => null),
          getUsersByTenant(tenantPublicId, accessToken).catch(() => []),
        ]);
        if (cancelled) return;
        setSettings(formSettings || {});
        setUsers(tenantUsers || []);
        // Opened for the user selected in the users list: preselect it, but only when the
        // picker offers it (a deleted user is not offered).
        if ((tenantUsers || []).some((u) => u.userPublicId === userPublicId && !u.isDeleted)) {
          setValue((current) => ({ ...current, userPublicId }));
        }
        // Without the settings the page cannot tell "not enabled" from "not reachable", so
        // the orderer is told rather than left with a silent unavailable message.
        if (!formSettings) toast.error("Could not load the offboarding page");
      } catch (error) {
        if (cancelled) return;
        setSettings({});
        toast.error("Could not load the offboarding page");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tenantPublicId, userPublicId]);

  const supported = !!settings?.supportsOffboarding;

  const handleSubmit = async () => {
    const validationError = validateOffboarding(value, todayIsoDate());
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const accessToken = await getAccessToken();
      const payload = buildOffboardingPayload(value, { source: "Dip", tenantPublicId });
      const result = await submitOffboarding(payload, accessToken);

      // Long-lived on purpose: the orderer has to be able to read and note the case
      // reference before the toast disappears, exactly like the hardware order page.
      toast.success(`Offboarding sent to Diflex. Case reference: ${result.caseReference}`, {
        duration: 12000,
      });

      // Automation is on but the schedule did not take: Diflex still has the case by mail.
      if (result.scheduled === false && settings?.offboardingAutomationEnabled) {
        toast.error(result.schedulingMessage || NOT_SCHEDULED_MESSAGE);
      }

      navigate(-1);
    } catch (error) {
      toast.error(submitErrorMessage(error));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (loaded && !enabled) {
    return <Navigate to={`/${agreementPublicId}/${tenantPublicId}/users`} replace />;
  }

  // The page is only meaningful once the settings and the users are there: until then the
  // orderer would see an empty user picker and a send button that cannot be used.
  if (!settings) {
    return (
      <PageShell>
        <p className="text-sm text-gray-600">Loading...</p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="flex flex-row items-center pb-4">
        <button onClick={() => navigate(-1)} className="text-black flex">
          <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
        </button>
        <h2 className="border-l border-mute-200 pl-4">Offboard user</h2>
      </div>

      <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
        {!supported && (
          <>
            <p className="text-sm text-gray-600">Offboarding is not available right now</p>
            <p className="text-sm text-gray-600">Contact Diflex support to offboard a user.</p>
          </>
        )}

        {supported && (
          <div className="grid grid-cols-2 gap-6 w-full">
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">
                {settings.offboardingAutomationEnabled
                  ? "The account will be disabled on the end date and deleted on the deletion date."
                  : "Diflex will perform the offboarding manually on the dates you choose."}
              </p>

              <OffboardingForm
                users={users}
                settings={settings}
                value={value}
                onChange={setValue}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-4 pt-4">
          <button className="btn btn-outline-primary" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!supported || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? "Sending..." : "Send offboarding"}
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default OffboardUserPage;
